import * as React from 'react';
import { FieldInputProps } from 'react-final-form';
import TFFIcon from './TFFIcon';

// Common props shared by both modes
interface TFFTextInputCommonProps {
  inputId: string;
  labelText: string;
  error?: boolean;
  errorText?: string;
  disabled?: boolean;
  required?: boolean;
  helperText?: string;
  maxLength?: number;
  extraStyle?: React.CSSProperties;
  labelColor?: string;
  leadingIconName?: string;
  inputType?: string;
}

// Props for react-final-form usage
// This should not be used for new created components, as redux-form is deprecated
// Currently only being used to support original functionality of old components
interface TFFTextInputReduxProps extends TFFTextInputCommonProps {
  inputFromRedux: FieldInputProps<any>;
  isBookingId?: boolean;
  // Standard input props should not be provided
  value?: never;
  onChange?: never;
  placeholder?: never;
}

// Props for standard controlled input usage
interface TFFTextInputStandardProps extends TFFTextInputCommonProps {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder?: string;
  // Redux props should not be provided
  inputFromRedux?: never;
  isBookingId?: never;
}

type TFFTextInputProps = TFFTextInputReduxProps | TFFTextInputStandardProps;

export const TFFTextInput: React.FC<TFFTextInputProps> = props => {
  const {
    inputId,
    labelText,
    disabled = false,
    required = false,
    helperText,
    maxLength,
    error = false,
    errorText,
    extraStyle,
    labelColor,
    leadingIconName,
    inputType = 'text',
  } = props;

  // Check if this is redux mode or standard mode
  const isReduxMode = 'inputFromRedux' in props && props.inputFromRedux !== undefined;

  // Common props for both modes
  const commonInputProps = {
    id: inputId,
    type: inputType,
    disabled,
    required,
    maxLength,
    'aria-invalid': error ? ('true' as const) : ('false' as const),
  };

  // Mode-specific props
  const modeSpecificProps = isReduxMode
    ? { ...props.inputFromRedux, className: props.isBookingId ? 'tff-id-input' : '' }
    : {
        value: props.value,
        onChange: props.onChange,
        placeholder: props.placeholder,
        className: inputType === 'date' ? 'tff-hide-native-icon' : '',
      };

  return (
    <label
      style={{ ...extraStyle }}
      htmlFor={inputId}
      className={`input input-text${disabled ? ' disabled' : ''} ${error ? ' error' : ''}`}
    >
      <span className="label" style={labelColor ? { color: `var(--${labelColor})` } : undefined}>{labelText}</span>
      {helperText && <span className="helpertext">{helperText}</span>}
      <span className="group" data-label="">
        {leadingIconName && <TFFIcon iconName={leadingIconName} wrapperClassName="icon-leading" color="color-theme-variant" />}
        <input {...commonInputProps} {...modeSpecificProps} />
      </span>
      {error && errorText && <p className="message">{errorText}</p>}
    </label>
  );
};

export default TFFTextInput;
